/** Where a tender came from. */
/** `rib` is no longer imported; tenders a person took over from it keep their source. */
export const SOURCES = [
  'oev',
  'nrw',
  'ted',
  'berlin',
  'vkberlin',
  'rib',
  'bund',
  'manual',
] as const;
export type Source = (typeof SOURCES)[number];

/**
 * Sources the import job fetches, in the order it fetches them; `manual` is entered by hand. A
 * procedure that two sources bring stays with the one named first (`DEDUPLICATED_SOURCES`).
 */
export const IMPORT_SOURCES = ['oev', 'nrw', 'ted', 'berlin', 'vkberlin', 'bund'] as const;
export type ImportSource = (typeof IMPORT_SOURCES)[number];

/**
 * Details of a tender a person may change. On an imported tender the import keeps a changed field
 * as the person wrote it (`editedFields`); the other fields follow the source.
 */
export const EDITABLE_FIELDS = [
  'title',
  'description',
  'buyerName',
  'placeCity',
  'state',
  'deadline',
  'participationDeadline',
  'documentsUrl',
  'cpv',
] as const;
export type EditableField = (typeof EDITABLE_FIELDS)[number];

/** Federal states by the codes `projects.object.state` uses. */
export const STATES = [
  'BW',
  'BY',
  'BE',
  'BB',
  'HB',
  'HH',
  'HE',
  'MV',
  'NI',
  'NW',
  'RP',
  'SL',
  'SN',
  'ST',
  'SH',
  'TH',
] as const;
export type State = (typeof STATES)[number];

export const NATURES = ['works', 'services', 'supplies'] as const;
export type Nature = (typeof NATURES)[number];

/** EU-wide above the thresholds or national below them. */
export const REGULATIONS = ['eu', 'national'] as const;
export type Regulation = (typeof REGULATIONS)[number];

/**
 * Work on a tender, from marking it as interesting to the answer of the buyer. A tender without
 * status is only in the stock and goes once its deadline has passed.
 */
export const TENDER_STATUSES = [
  'review',
  'preparing',
  'submitted',
  'waiting',
  'won',
  'lost',
  'dropped',
] as const;
export type TenderStatus = (typeof TENDER_STATUSES)[number];

/** States after the submission: the weekly check for an answer runs in these. */
export const AWAITING_STATUSES: readonly TenderStatus[] = ['submitted', 'waiting'];
/** States in which the deadline still matters. */
export const OPEN_STATUSES: readonly TenderStatus[] = ['review', 'preparing'];

export interface Lot {
  id: string;
  title: string | null;
  deadline: string | null;
  cpv: string[];
}

/** A row of the overview. */
export interface TenderListItem {
  id: string;
  source: Source;
  title: string;
  buyerName: string | null;
  placeCity: string | null;
  state: State | null;
  cpv: string[];
  nature: Nature | null;
  regulation: Regulation | null;
  procedure: string | null;
  deadline: string | null;
  participationDeadline: string | null;
  publishedOn: string | null;
  estimatedValue: number | null;
  currency: string | null;
  status: TenderStatus | null;
  ownerId: string | null;
  /** Day the owner checks for an answer of the buyer, after the submission. */
  feedbackDueOn: string | null;
}

export interface TenderPage {
  items: TenderListItem[];
  total: number;
}

/** What the AI made of a tender: suggestion until a person takes the checklist over. */
export interface TenderSummary {
  summary: string;
  documents: { title: string; description: string | null }[];
  /** `notice`: details of the notice only; `files`: with the tender documents. */
  basis: 'notice' | 'files';
  /** With `files`: documents the AI could not read, e.g. scanned PDFs, as paths. */
  unread?: string[];
  createdAt: string;
  createdBy: string | null;
}

export interface Tender extends TenderListItem {
  sourceKey: string;
  /** Fields a person changed on an imported tender; the import leaves them as they are. */
  editedFields: EditableField[];
  noticeId: string | null;
  description: string | null;
  buyerCity: string | null;
  placePostalCode: string | null;
  nuts: string[];
  questionsDeadline: string | null;
  documentsUrl: string | null;
  sourceUrl: string | null;
  lots: Lot[];
  requirements: string[];
  closedAt: string | null;
  markedAt: string | null;
  submittedAt: string | null;
  projectId: string | null;
  memberIds: string[];
  summary: TenderSummary | null;
  summaryFull: TenderSummary | null;
  canWrite: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Proof the company keeps ready for tenders, e.g. a certificate of liability insurance. */
export interface Evidence {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  issuedOn: string | null;
  validUntil: string | null;
  responsibleId: string | null;
  link: string | null;
  /** Checklist points of tenders in work that use it. */
  usage: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** A point of the checklist for the submission. */
export interface ChecklistItem {
  id: string;
  tenderId: string;
  title: string;
  description: string | null;
  origin: 'ai' | 'manual';
  done: boolean;
  position: number;
  evidenceIds: string[];
  fileCount: number;
  version: number;
}

/** State of an import source in the active company. */
export interface SourceRun {
  source: ImportSource;
  enabled: boolean;
  lastDay: string | null;
  lastRunAt: string | null;
  lastError: string | null;
  lastCount: number | null;
}
